// server/browser/src/lib/auth.ts
//
// Single login for the whole SPA. OIDC Authorization Code + PKCE against
// the cloud's IdP (the same realm/public client desktop + CLI use:
// neos-app). One token is acquired once and sent as a bearer to every
// /v1/* surface — account/billing, /v1/remote/*, and /v1/admin/*. There
// is no second sign-in to enter the remote app or the admin console;
// admin *authorization* is still enforced server-side on the group claim.
//
// Tokens live in sessionStorage so a closed tab drops them.

import { platformOrNull } from "./remote/platform";

const TOKEN_KEY = "neos_access_token";
const ID_TOKEN_KEY = "neos_id_token";
const STATE_KEY = "neos_oidc_state";
const VERIFIER_KEY = "neos_oidc_verifier";
const RETURN_TO_KEY = "neos_return_to";

/** Dispatched whenever the token set changes; the shell re-reads identity. */
export const AUTH_EVENT = "neos:auth";

// sessionStorage (above) is the synchronous source of truth for hot
// reads. When a native PlatformAdapter is registered (iOS), mirror token
// writes through it so they also land in the Keychain and survive a cold
// relaunch. The browser adapter delegates its token methods straight back
// into this module, so `mirroring` guards against
// setAccessToken → adapter.setAccessToken → setAccessToken recursion.
// No adapter (browser site/admin) → no-op.
let mirroring = false;
function mirrorToPlatform(
  call: (p: NonNullable<ReturnType<typeof platformOrNull>>) => Promise<void>,
): void {
  if (mirroring) return;
  const p = platformOrNull();
  if (!p) return;
  // `mirroring` is cleared synchronously below. The browser adapter
  // re-enters this module *synchronously* inside `call(p)` (it delegates
  // its token methods back to set/clear here, before its first await), so
  // by the time `call(p)` returns the re-entry is done and the guard can
  // drop — without blocking a legitimate sequential write (e.g.
  // completeLogin's setAccessToken then setIdToken). The adapter's async
  // tail (iOS Keychain invoke) settles later and never touches the flag.
  mirroring = true;
  try {
    void Promise.resolve(call(p)).catch(() => {
      /* native-side failures never block the web flow */
    });
  } finally {
    mirroring = false;
  }
}

export interface OidcConfig {
  authorizeUrl: string;
  tokenUrl: string;
  clientId: string;
  redirectUri: string;
  clientSecret?: string;
  endSessionUrl?: string;
  scope?: string;
}

export function getAccessToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
  mirrorToPlatform((p) => p.setAccessToken(token));
}

export function getIdToken(): string | null {
  try {
    return sessionStorage.getItem(ID_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setIdToken(token: string): void {
  try {
    sessionStorage.setItem(ID_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
  mirrorToPlatform((p) => p.setIdToken(token));
}

export function isSignedIn(): boolean {
  return !!getAccessToken();
}

export interface Identity {
  name: string;
  email: string;
  sub: string;
  groups: string[];
}

/** Pure client-side JWT payload decode for *display / cosmetic route
 *  guards only* — never trust for authorization (the cloud validates
 *  every request and re-checks the group claim on /v1/admin/*). */
export function getIdentity(): Identity | null {
  const token = getAccessToken();
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
    const payload = JSON.parse(atob(b64 + pad)) as Record<string, unknown>;
    const sub = String(payload.sub ?? "");
    const email = String(payload.email ?? "");
    const name = String(payload.preferred_username ?? payload.name ?? email ?? sub);
    const groups = Array.isArray(payload.groups)
      ? (payload.groups as unknown[]).map(String)
      : [];
    return { name, email, sub, groups };
  } catch {
    return null;
  }
}

export function clearLocalTokens(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ID_TOKEN_KEY);
  } catch {
    /* ignore */
  }
  mirrorToPlatform((p) => p.clearTokens());
}

export const signOut = clearLocalTokens;

export function endSessionWithIdp(endSessionUrl: string, redirectUri: string): void {
  const idToken = getIdToken();
  clearLocalTokens();
  // Route the post-logout navigation through the platform seam: on iOS the
  // logout URL must open in Safari (the WebView origin is local, so a full-page
  // nav would strand the app); on browser the adapter — or its absence —
  // resolves to the same-window window.location.assign used before.
  const p = platformOrNull();
  const open = (u: string): void => {
    if (p) void p.openExternalUrl(u);
    else if (typeof window !== "undefined") window.location.assign(u);
  };
  if (!endSessionUrl || !idToken) {
    open(redirectUri);
    return;
  }
  const params = new URLSearchParams({
    id_token_hint: idToken,
    post_logout_redirect_uri: redirectUri,
  });
  open(`${endSessionUrl}?${params.toString()}`);
}

// ---- PKCE -------------------------------------------------------------

const ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";

export function randomVerifier(length = 64): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  return out;
}

function base64UrlEncode(bytes: ArrayBuffer): string {
  let s = "";
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i++) s += String.fromCharCode(view[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(digest);
}

export async function startLogin(config: OidcConfig, returnTo?: string): Promise<string> {
  const state = randomVerifier(32);
  const verifier = randomVerifier(64);
  const challenge = await challengeFor(verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: "code",
    redirect_uri: config.redirectUri,
    scope: config.scope || "openid email profile",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return `${config.authorizeUrl}?${params.toString()}`;
}

/**
 * Start the OIDC sign-in: build the authorize URL (PKCE state stashed in
 * sessionStorage by `startLogin`) and hand it to the platform seam. On iOS the
 * adapter opens Safari — a full-page nav would leave the Tauri WebView, whose
 * origin is local — and the `neos://callback` deep link completes the
 * round-trip via `completeLogin`. On browser the seam is window.location.assign,
 * identical to what every call site did inline before this helper.
 */
export async function beginLogin(config: OidcConfig, returnTo?: string): Promise<void> {
  const url = await startLogin(config, returnTo);
  const p = platformOrNull();
  if (p) await p.openExternalUrl(url);
  else if (typeof window !== "undefined") window.location.assign(url);
}

export interface CallbackParams {
  code: string;
  state: string;
}

export async function completeLogin(
  config: OidcConfig,
  params: CallbackParams,
): Promise<{ token: string; returnTo: string | null }> {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!expectedState || params.state !== expectedState) {
    throw new Error("OIDC state mismatch — possible CSRF, please retry.");
  }
  if (!verifier) throw new Error("Missing PKCE verifier — please retry sign-in.");
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code: params.code,
    redirect_uri: config.redirectUri,
    client_id: config.clientId,
    code_verifier: verifier,
  });
  if (config.clientSecret) body.set("client_secret", config.clientSecret);
  const res = await fetch(config.tokenUrl, {
    method: "POST",
    headers: {
      "content-type": "application/x-www-form-urlencoded",
      accept: "application/json",
    },
    body: body.toString(),
  });
  if (!res.ok) throw new Error(`Token exchange failed (${res.status})`);
  const json = (await res.json()) as { access_token?: string; id_token?: string };
  if (!json.access_token) throw new Error("Token response did not include access_token");
  setAccessToken(json.access_token);
  if (json.id_token) setIdToken(json.id_token);
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return { token: json.access_token, returnTo };
}
